import { useState, useCallback } from 'react';
import type React from 'react';

// ── Types ──────────────────────────────────────────────────────────────────

interface UseKeyboardSelectableListOptions<T> {
  /** Items to navigate */
  items: T[];
  /** Called when an item is activated (Enter or click) */
  onSelect: (item: T, index: number) => void;
  /** Initial highlighted index (default 0) */
  initialIndex?: number;
  /** Whether to wrap at the ends (default false) */
  loop?: boolean;
}

interface ItemProps {
  role: 'option';
  'aria-selected': boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

interface ListProps {
  role: 'listbox';
  'aria-activedescendant': string | undefined;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

interface UseKeyboardSelectableListResult {
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  getItemProps: (index: number) => ItemProps;
  getListProps: () => ListProps;
}

// ── Hook ───────────────────────────────────────────────────────────────────

/**
 * useKeyboardSelectableList — keyboard navigation (Arrow, Home, End, Enter)
 * for a list of items. Pairs with List.Item (using the `highlighted` and
 * `role` props) for full keyboard navigation in menus and command palettes.
 *
 * ```ts
 * const { highlightedIndex, getItemProps, getListProps } =
 *   useKeyboardSelectableList({
 *     items: results,
 *     onSelect: (item) => navigate(item.id),
 *     loop: true,
 *   });
 *
 * return (
 *   <ul {...getListProps()}>
 *     {results.map((item, i) => (
 *       <List.Item
 *         key={item.id}
 *         highlighted={i === highlightedIndex}
 *         role="option"
 *         {...getItemProps(i)}
 *       >
 *         {item.title}
 *       </List.Item>
 *     ))}
 *   </ul>
 * );
 * ```
 */
export function useKeyboardSelectableList<T>({
  items,
  onSelect,
  initialIndex = 0,
  loop = false,
}: UseKeyboardSelectableListOptions<T>): UseKeyboardSelectableListResult {
  const [highlightedIndex, setHighlightedIndex] = useState(initialIndex);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const lastIndex = items.length - 1;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            if (prev >= lastIndex) return loop ? 0 : prev;
            return prev + 1;
          });
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setHighlightedIndex((prev) => {
            if (prev <= 0) return loop ? lastIndex : prev;
            return prev - 1;
          });
          break;
        }
        case 'Home': {
          e.preventDefault();
          setHighlightedIndex(0);
          break;
        }
        case 'End': {
          e.preventDefault();
          setHighlightedIndex(lastIndex);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (items[highlightedIndex] !== undefined) {
            onSelect(items[highlightedIndex], highlightedIndex);
          }
          break;
        }
        default:
          break;
      }
    },
    [items, highlightedIndex, loop, onSelect],
  );

  const getItemProps = useCallback(
    (index: number): ItemProps => ({
      role: 'option',
      'aria-selected': index === highlightedIndex,
      onMouseEnter: () => setHighlightedIndex(index),
      onClick: () => {
        setHighlightedIndex(index);
        if (items[index] !== undefined) {
          onSelect(items[index], index);
        }
      },
    }),
    [highlightedIndex, items, onSelect],
  );

  const getListProps = useCallback(
    (): ListProps => ({
      role: 'listbox',
      'aria-activedescendant': undefined,
      onKeyDown: handleKeyDown,
    }),
    [handleKeyDown],
  );

  return {
    highlightedIndex,
    setHighlightedIndex,
    getItemProps,
    getListProps,
  };
}
